import React, { useState, useEffect } from 'react';
import { Grid, styled } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { send_request } from '../../../utils/Request';
import { useStatusPalette } from '../../../utils/statusColors';
import { buildSessionStatusStyles, formatRelativeTime } from '../agentSessionsPanelUtils';
import { DashboardCard, CardTitle, StyledSkeleton } from './CommonComponents';

const SessionRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 10px',
  borderRadius: 6,
  cursor: 'pointer',
  transition: 'background 120ms ease',
  gap: 12,
  '&:hover': {
    background: theme.palette.action.hover,
  },
}));

const StatusChip = styled('span')({
  fontSize: 11,
  fontWeight: 500,
  padding: '1px 7px',
  borderRadius: 9999,
  whiteSpace: 'nowrap',
  lineHeight: '18px',
});

const ViewAllLink = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  color: theme.palette.text.secondary,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 0',
  transition: 'color 120ms ease',
  '&:hover': {
    color: theme.palette.text.primary,
  },
}));

export default function RecentConversationsCard({ agents }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const palette = useStatusPalette();
  const statusStyles = buildSessionStatusStyles(palette);

  useEffect(() => {
    const fetchRecentSessions = async () => {
      try {
        const response = await send_request(
          'project-service/agent/recent-sessions',
          '',
          { limit: 5 },
          'GET'
        );
        setSessions(response?.data || []);
      } catch (e) {
        console.error('Failed to load recent sessions:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchRecentSessions();
  }, []);

  const getAgentName = agentId => {
    if (!agents || !agentId) return 'Agent';
    const agent = agents.find(a => a.agentId === agentId);
    return agent?.name || 'Agent';
  };

  if (loading) {
    return (
      <DashboardCard container rowGap="8px">
        <Grid item xs={12}>
          <CardTitle>Recent Conversations</CardTitle>
        </Grid>
        <Grid item xs={12}>
          {[0, 1, 2].map(i => (
            <StyledSkeleton key={i} variant="rectangular" height="40px" margin="0 0 4px 0" />
          ))}
        </Grid>
      </DashboardCard>
    );
  }

  if (sessions.length === 0) {
    return (
      <DashboardCard container rowGap="8px">
        <Grid item xs={12}>
          <CardTitle>Recent Conversations</CardTitle>
        </Grid>
        <Grid item xs={12}>
          <div style={{
            color: 'var(--color-text-secondary, #71717a)',
            fontSize: 13,
            padding: '20px 0',
            textAlign: 'center',
          }}>
            No conversations yet. Chat with an agent to get started.
          </div>
        </Grid>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard container rowGap="4px">
      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <CardTitle>Recent Conversations</CardTitle>
        <ViewAllLink onClick={() => navigate('/agents/chats')}>
          View all <ArrowForwardIcon sx={{ fontSize: 14 }} />
        </ViewAllLink>
      </Grid>
      <Grid item xs={12}>
        {sessions.map(session => {
          const style = statusStyles[session.status] || statusStyles.COMPLETED;
          return (
            <SessionRow
              key={session.chatSessionId}
              onClick={() => navigate(`/agents/chats?agent=${session.agentId}&sessionId=${session.chatSessionId}`)}
            >
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  title={getAgentName(session.agentId)}
                  style={{
                    fontWeight: 500,
                    fontSize: 12,
                    color: 'var(--color-text-secondary, #71717a)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexShrink: 1,
                    maxWidth: 'clamp(96px, 35%, 220px)',
                    minWidth: 0,
                  }}
                >
                  {getAgentName(session.agentId)}
                </span>
                <span style={{
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: '1 1 auto',
                  minWidth: 0,
                }}>
                  {session.title || 'Untitled'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <StatusChip style={{
                  color: style.color,
                  backgroundColor: style.bg,
                  border: `1px solid ${style.border}`,
                }}>
                  {style.label}
                </StatusChip>
                <span style={{ fontSize: 12, color: 'var(--color-text-secondary, #a1a1aa)', minWidth: 28, textAlign: 'right' }}>
                  {formatRelativeTime(session.updatedAt)}
                </span>
              </div>
            </SessionRow>
          );
        })}
      </Grid>
    </DashboardCard>
  );
}
